import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKENS } from "constants/AuthConstant";
import { APP_TOKENS } from "constants/AppConstant";
import AmplifyAuthService from "services/AmplifyAuthService";
import AmplifyAPIService from "services/AmplifyAPIService";

import {
  getUser,
  billingsByCompanyIDAndStartDate,
  listUsers,
  listLocations,
  listVariants,
} from "graphql_api/queries";

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  access: "",
  token: localStorage.getItem(AUTH_TOKENS.token) || null,
  userName: localStorage.getItem(AUTH_TOKENS.name) || null,
  companyName: localStorage.getItem(AUTH_TOKENS.company) || null,
  role: localStorage.getItem(AUTH_TOKENS.role) || null,
  isNew: localStorage.getItem(AUTH_TOKENS.isNew) || null,
};

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;

    try {
      const response = await AmplifyAuthService.signInEmailRequest(
        email,
        password
      );
      if (response.username) {
        const token = response.signInUserSession.accessToken.jwtToken;

        const id = response.attributes["custom:userID"];
        const userData = await AmplifyAPIService.getModel(getUser, id);

        const role = userData.data.getUser.role;
        const name =
          userData.data.getUser.name + " " + userData.data.getUser.familyName;
        const company = userData.data.getUser.company.companyName;
        const country = userData.data.getUser.company.mainCountry;
        const isNew = userData.data.getUser.company.isNew;
        localStorage.setItem(AUTH_TOKENS.token, token);
        localStorage.setItem(AUTH_TOKENS.name, name);
        localStorage.setItem(AUTH_TOKENS.company, company);
        localStorage.setItem(AUTH_TOKENS.role, role);
        localStorage.setItem(AUTH_TOKENS.isNew, isNew);

        return { token, name, company, role, country, isNew };
      } else {
        return rejectWithValue(response.message?.replace("Amplify: ", ""));
      }
    } catch (err) {
      console.log(err);
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/signUp",
  async (data, { rejectWithValue }) => {
    const {
      annualIncome,
      businessType,
      code,
      companyName,
      consultant,
      confirm,
      currency,
      email,
      endDate,
      period,
      discount,
      lastName,
      mainCountry,
      name,
      numEmployees,
      password,
      planID,
      price,
      remember,
      startDate,
      subscriptionID,
      stripeCustomer,
      stripePriceID,
      trial,
      paymentReceived,
      billingID,
      phoneNumber,
      phoneCode,
      ipAddress,
      userAgent,
    } = data;
    try {
      const response = await AmplifyAuthService.signUpEmailRequest(
        annualIncome,
        businessType,
        code,
        companyName,
        consultant,
        confirm,
        currency,
        email,
        endDate,
        period,
        discount,
        lastName,
        mainCountry,
        name,
        numEmployees,
        password,
        planID,
        price,
        remember,
        startDate,
        subscriptionID,
        stripeCustomer,
        stripePriceID,
        trial,
        paymentReceived,
        billingID,
        phoneNumber,
        phoneCode,
        ipAddress,
        userAgent
      );

      if (response.user) {
        return "SUCCESS";
      } else {
        return response.message;
      }
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("auth/signOut", async () => {
  const response = await AmplifyAuthService.signOutRequest();
  localStorage.removeItem(AUTH_TOKENS.token);
  localStorage.removeItem(AUTH_TOKENS.name);
  localStorage.removeItem(AUTH_TOKENS.company);
  localStorage.removeItem(AUTH_TOKENS.role);
  localStorage.removeItem(AUTH_TOKENS.mixLayout);
  localStorage.removeItem(AUTH_TOKENS.adminMode);
  localStorage.removeItem(APP_TOKENS.selectedLocation);
  localStorage.removeItem(APP_TOKENS.selectedCountry);
  localStorage.removeItem(APP_TOKENS.selectedCashier);
  localStorage.removeItem(APP_TOKENS.isNew);

  return response.data;
});

export const permissionsValidation = createAsyncThunk(
  "auth/permissionsValidation",
  async () => {
    try {
      let access;

      const info = await AmplifyAuthService.currentSessionRequest();
      const id = info.idToken.payload["custom:userID"];
      const userData = await AmplifyAPIService.getModel(getUser, id);
      if (
        userData.data.getUser.role === 1 ||
        userData.data.getUser.role === 2
      ) {
        access = "confirm";
      } else {
        access = "denied";
      }

      return { access };
    } catch (err) {
      console.log("Error validation user permissions ", err);
    }
  }
);

export const planValidation = createAsyncThunk(
  "auth/planValidation",
  async () => {
    try {
      let access;
      let surplus;
      let trial;

      const info = await AmplifyAuthService.currentSessionRequest();
      const companyID = info.idToken.payload["custom:companyID"];

      const variables = {
        companyID: companyID,
        sortDirection: "DESC",
      };
      const billingData = await AmplifyAPIService.getModelFilter(
        billingsByCompanyIDAndStartDate,
        variables
      );

      const locationsData = await AmplifyAPIService.fetchModel(listLocations);
      const locationsLength = locationsData.data.listLocations.items.length;
      const usersData = await AmplifyAPIService.fetchModel(listUsers);
      const usersLength = usersData.data.listUsers.items.length;
      const variantsData = await AmplifyAPIService.fetchModel(listVariants);
      const variantsLength = variantsData.data.listVariants.items.length;

      if (billingData.data.billingsByCompanyIDAndStartDate.items.length !== 0) {
        trial = billingData.data.billingsByCompanyIDAndStartDate.items[0].trial;

        if (
          new Date(
            billingData.data.billingsByCompanyIDAndStartDate.items[0].endDate
          ) < new Date() ||
          billingData.data.billingsByCompanyIDAndStartDate.items[0].active ===
            false
        ) {
          access = "denied";
        } else {
          access = "confirm";
        }

        if (
          locationsLength >
            billingData.data.billingsByCompanyIDAndStartDate.items[0].plan
              .locations ||
          usersLength >
            billingData.data.billingsByCompanyIDAndStartDate.items[0].plan
              .users ||
          variantsLength >
            billingData.data.billingsByCompanyIDAndStartDate.items[0].plan
              .variants
        ) {
          surplus = true;
        } else {
          surplus = false;
        }
      } else {
        access = "denied";
        surplus = true;
        trial = false;
      }
      return [access, surplus, trial];
    } catch (err) {
      console.log("Error validation account permissions ", err);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      //state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    hideLoading: (state) => {
      state.loading = false;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload.token;
        state.userName = action.payload.name;
        state.companyName = action.payload.company;
        state.role = action.payload.role;
        state.isNew = action.payload.isNew;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
        state.userName = null;
        state.companyName = null;
        state.role = null;
        state.isNew = null;
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
        state.userName = null;
        state.companyName = null;
        state.role = null;
        state.isNew = null;
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        //state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(permissionsValidation.pending, (state) => {})
      .addCase(permissionsValidation.fulfilled, (state, action) => {
        state.access = action.payload.access;
      })
      .addCase(permissionsValidation.rejected, (state, action) => {
        state.access = action.payload.access;
      });
  },
});

export const {
  showLoading,
  hideLoading,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showErrorMessage,
  hideErrorMessage,
  signInSuccess,
  authenticated,
} = authSlice.actions;

export default authSlice.reducer;
